import { useState } from "react";
import { FileTextIcon, Maximize2Icon, StarIcon } from "lucide-react";

import { ResumeActionsMenu } from "./ResumeActionsMenu";
import { useUserSheet } from "../userSheetContext";

import { Column, DocumentPreview, Row, Text } from "@/components";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

import { dates } from "@/lib/dates";
import { cn } from "@/lib/utils";
import { ResumeFileType } from "@/types";

// Enrichment-generated resumes are always titled "<name> - Laborup Resume.pdf".
const ENRICHED_RESUME_TITLE_MARKER = "Laborup Resume";

export const ResumeCard = ({
  isPrimary,
  onOpenInline,
  onResumeUpdated,
  primaryResumeId,
  resumeFile,
  userId,
}: {
  isPrimary: boolean;
  onOpenInline?: (resumeFile: ResumeFileType) => void;
  onResumeUpdated: () => void;
  primaryResumeId: number | null;
  resumeFile: ResumeFileType;
  userId: number;
}) => {
  /* Context */
  const { isReadOnly } = useUserSheet();

  /* React Hooks */
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  /* Helpers */
  const uploadedAt = (resumeFile as { createdAt?: string }).createdAt;
  const uploadedLabel = uploadedAt
    ? (dates.formatIsoDateForUser(uploadedAt) ??
      dates.timeAgoForJob(uploadedAt))
    : null;
  const isEnriched = (resumeFile.title || "").includes(
    ENRICHED_RESUME_TITLE_MARKER,
  );

  /* Render */
  const renderThumbnail = () => {
    return (
      <Column className="group relative h-44 w-full overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
        <div className="pointer-events-none absolute left-1/2 top-3 w-[520px] origin-top -translate-x-1/2 scale-[0.48] opacity-70">
          <DocumentPreview href={resumeFile.url ?? ""} pageWidthScale={1} />
        </div>
        <Column className="absolute inset-0 items-center justify-center gap-2 bg-slate-900/0 px-3 opacity-0 transition-all group-hover:bg-slate-900/30 group-hover:opacity-100">
          {onOpenInline ? (
            <Button
              className="h-8 w-full text-xs"
              onClick={() => {
                onOpenInline(resumeFile);
              }}
              type="button"
              variant="outline"
            >
              <FileTextIcon className="h-3.5 w-3.5" />
              Open inline
            </Button>
          ) : null}
          <Button
            className="h-8 w-full text-xs"
            onClick={() => {
              setIsPreviewOpen(true);
            }}
            type="button"
            variant="outline"
          >
            <Maximize2Icon className="h-3.5 w-3.5" />
            Open preview
          </Button>
        </Column>
        {isPrimary ? (
          <StarIcon className="absolute right-3 top-3 h-5 w-5 fill-yellow-500 text-yellow-500" />
        ) : null}
      </Column>
    );
  };

  const render = () => {
    return (
      <>
        <Column
          className={cn(
            "w-full min-w-[180px] max-w-60",
            "flex-none gap-3 rounded-xl border border-slate-200 bg-white p-3 shadow-sm",
          )}
        >
          {renderThumbnail()}
          <Column className="min-w-0 gap-2 overflow-hidden">
            <Row className="min-w-0 items-center justify-between gap-2">
              <Row className="min-w-0 flex-1 items-center gap-2">
                <FileTextIcon className="h-4 w-4 shrink-0 text-slate-400" />
                <Text className="min-w-0 truncate text-sm font-medium text-slate-700">
                  {resumeFile.title || "Resume"}
                </Text>
              </Row>
              {isReadOnly ? null : (
                <ResumeActionsMenu
                  onResumeUpdated={onResumeUpdated}
                  primaryResumeId={primaryResumeId}
                  resumeFile={resumeFile}
                  userId={userId}
                />
              )}
            </Row>
            <Row className="items-center gap-2">
              <Text
                className={cn(
                  "rounded-md px-2 py-0.5 text-xs font-medium",
                  isEnriched
                    ? "bg-green-50 text-green-700"
                    : "bg-slate-100 text-slate-600",
                )}
              >
                {isEnriched ? "Enriched" : "Uploaded"}
              </Text>
              {uploadedLabel ? (
                <Text className="text-xs font-normal text-slate-400">
                  {uploadedLabel}
                </Text>
              ) : null}
            </Row>
          </Column>
        </Column>

        <Dialog onOpenChange={setIsPreviewOpen} open={isPreviewOpen}>
          <DialogContent className="flex h-[92vh] max-w-5xl flex-col gap-0 overflow-hidden p-0">
            <DialogTitle className="sr-only">Resume preview</DialogTitle>
            <div className="min-h-0 flex-1 overflow-y-auto bg-slate-50 p-4">
              <DocumentPreview
                centered
                href={resumeFile.url ?? ""}
                pageGap={12}
              />
            </div>
          </DialogContent>
        </Dialog>
      </>
    );
  };

  return render();
};
